import map from 'lodash/map'
import React, { FC } from 'react'

import { Image } from '@Components/Image'
import { Link } from '@Components/Link'
import { StarRating } from '@Components/StarRating'
import { ConditionalWrap } from '@Globals/ConditionalWrap'
import { HorizontalCarousel } from '@Shared/Components/Carousels/HorizontalCarousel/HorizontalCarousel'

import styles from './Gallery.module.scss'

export interface Slide {
  author: string
  date: Date
  headline: string
  image: string
  link: string
  linkNewTab: boolean
  /** Range 0 - 5, can have decimals */
  rating: number
  text: string
}

export const SlideItem = ({
  author,
  date,
  headline,
  image,
  link,
  linkNewTab,
  rating,
  text,
}: Slide) => {
  const hasInfo = headline || text
  const hasReview = rating >= 0 && author !== '' && date
  const hasAuthorWithoutReview = author && !hasReview

  return (
    // condtionally wrap in a Link
    <ConditionalWrap
      condition={!!link}
      // eslint-disable-next-line react/no-unstable-nested-components
      wrap={children => (
        <Link href={link} target={linkNewTab ? '_blank' : undefined}>
          {children}
        </Link>
      )}
    >
      <div className={styles.slideWrapper}>
        {/* Image wrap */}
        <div className={styles.imageWrap}>
          <Image options={{ path: image }} />
        </div>

        {/* Info wrap */}
        {hasInfo && (
          <div className={styles.textWrap}>
            {/* Rating, name && date */}
            {hasReview && (
              <div className={styles.leftSide}>
                {rating && (
                  <div className={styles.ratingWrap}>
                    <StarRating rating={rating} />
                  </div>
                )}
                {author && <span className={styles.authorName}>{author}</span>}
                {date && <span className={styles.date}>{date.toLocaleDateString()}</span>}
              </div>
            )}

            {hasAuthorWithoutReview && (
              <div className={styles.leftSide}>
                <span className={styles.authorName}>{author}</span>
              </div>
            )}

            {/* Title && text */}
            <div className={styles.rightSide}>
              <span className={styles.headline}>{headline}</span>
              <span className={styles.slideTextBlock} dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          </div>
        )}
      </div>
    </ConditionalWrap>
  )
}

export interface GalleryProps {
  id?: string
  slides: Slide[]
}

export const Gallery: FC<GalleryProps> = ({ id, slides }) => (
  <HorizontalCarousel
    alwaysShowButtons
    buttonClassName={styles.sliderButtons}
    carouselClassName={styles.carousel}
    className={styles.gallery}
    showButtons
    snap
    unevenChildWidth
  >
    {map(slides, (slide, index) => (
      <span key={`${id}_${index}`} className={styles.galleryItem}>
        <span className={styles.slideIndex}>{`${index + 1}/${slides.length}`}</span>
        <SlideItem {...slide} />
      </span>
    ))}
  </HorizontalCarousel>
)
